import { useEffect, useRef } from 'react';
import { useApiConfig } from './ExperienceConfigProvider';
import type { Result } from '../types/result';
import { fetchOfferingDetailFields } from '../utils/data-fetch/fetchOfferingDetailFields';
import {
  needsComparisonDetailHydration,
  resolveOfferingIdForDetail,
} from '../components/ComparisonModal/comparisonDetailHydration';

/**
 * Hydrates drawer-detail fields (TCPA, …) onto compared programs that still
 * have an empty `tcpa` from the `/match` card projection.
 *
 * Owned by {@link ComparisonProvider} so only one hydrator runs per
 * selection context.
 */
export function useComparisonDetailHydration(
  programsToCompare: Result[],
  patchProgramInCompare: (programId: number, patch: Partial<Result>) => void,
): void {
  const apiConfig = useApiConfig();
  const inFlightRef = useRef(new Map<number, AbortController>());
  const settledRef = useRef(new Set<number>());

  useEffect(() => {
    const selectedIds = new Set(programsToCompare.map((p) => p.programId));

    Array.from(inFlightRef.current.entries()).forEach(
      ([programId, controller]) => {
        if (!selectedIds.has(programId)) {
          controller.abort();
          inFlightRef.current.delete(programId);
          settledRef.current.delete(programId);
        }
      },
    );

    for (const program of programsToCompare) {
      if (!needsComparisonDetailHydration(program)) {
        settledRef.current.add(program.programId);
        continue;
      }
      if (settledRef.current.has(program.programId)) {
        continue;
      }
      if (inFlightRef.current.has(program.programId)) {
        continue;
      }

      const offeringId = resolveOfferingIdForDetail(program);
      if (!offeringId) {
        settledRef.current.add(program.programId);
        continue;
      }

      const controller = new AbortController();
      inFlightRef.current.set(program.programId, controller);
      const programId = program.programId;

      void fetchOfferingDetailFields(offeringId, {
        signal: controller.signal,
        apiConfig,
      })
        .then((fields) => {
          if (controller.signal.aborted) {
            return;
          }
          // `null` = transport/policy failure — leave unsettled so a later
          // effect can retry. `{}` = OK response with no mappable fields.
          if (fields === null) {
            return;
          }
          if (Object.keys(fields).length > 0) {
            patchProgramInCompare(programId, fields);
          }
          settledRef.current.add(programId);
        })
        .finally(() => {
          inFlightRef.current.delete(programId);
        });
    }
  }, [programsToCompare, patchProgramInCompare, apiConfig]);

  useEffect(
    () => () => {
      Array.from(inFlightRef.current.values()).forEach((controller) => {
        controller.abort();
      });
      inFlightRef.current.clear();
      settledRef.current.clear();
    },
    [],
  );
}
